/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

// 3rd party libraries
import React, {
  useState,
  useEffect,
  KeyboardEventHandler,
  useRef
} from 'react';
import { Form, Input, Typography } from 'antd';
import { Button } from '@/uiKit';

// Local imports
import i18n from '@utils/i18n';
import AppUtil from '@utils/AppUtil';
import AppConstants from '@utils/AppConstants';
import ComponentFactory from '@utils/ComponentFactory';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/provider/auth';
import { trim } from 'ramda';
import { useSelector } from 'react-redux';
import UnauthorizedPageLayout from '@/components/unauthorizedPageLayout/UnauthorizedPageLayout';
import EmailIcon from '@/components/icons/EmailIcon';
import { trackEvent } from '@/tracker';
import { AnalyticsEvents } from '@/analytics/events';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { userSelector } from '../../stores/slices/user';
import {
  delayExponentialBackoff,
  retryPromiseWithDelay
} from '../../utils/promise';
import styles from '../Signup/Signup.module.less';
import { LoadingStateType } from '../Signup/Signup';
import { shouldRetryQueryOnCommonErrors } from '../../utils/query';

type LocationState = { from?: { pathname?: string } };

const { Text } = Typography;

// Component i18n
const i18nt = (s: string, d?: string) => i18n.t('Login', s, d);

export function VerificationLink({ email }: { email: string }) {
  return (
    <div className={styles.verifyContainer}>
      <div className={styles.verifyImage}>
        <EmailIcon />
      </div>
      <Typography.Title level={3} className={styles.welcomeText}>
        Check your email
      </Typography.Title>
      <Typography.Text
        className={styles.welcomeSub}
        data-cy="email-sent-confirmation"
      >
        To verify your email address, click on the confirmation link sent to you
        at <span className={styles.email}>{email}</span>.
      </Typography.Text>
    </div>
  );
}

// Component implementation
function Login() {
  // State Hooks
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { username } = useSelector(userSelector);
  const [statusMessage, setStatusMessage] = useState('');
  // state can be: {'', 'loading', 'verify'}
  const [state, setState] = useState('');

  const lState = location.state as LocationState;

  // Reference to the email input box
  // @ts-ignore
  const inputRef = useRef<Input>(null);

  // Auto Focus on the email input
  useEffect(() => {
    document.body.classList.add('login');
    inputRef.current?.focus();
    return () => {
      document.body.classList.remove('login');
    };
  });

  trackEvent(AnalyticsEvents.LOGIN.LoginPage);
  useDocumentTitle(AppUtil.preparePageTitle('Login'));

  useEffect(() => {
    // if user is logged in we are redirecting
    if (username) {
      const navigateUrl = lState?.from?.pathname || '/';
      navigate(navigateUrl, { replace: true });
    }
  }, [navigate, username, lState]);

  // Login button handler
  const onLogin = async () => {
    const newEmail = trim(email);

    const personalEmail = AppUtil.isPersonalEmail(newEmail);
    if (personalEmail) {
      setStatusMessage('Please login with your corporate email address.');
      return;
    }

    if (newEmail.length) {
      if (!AppUtil.validateEmail(newEmail)) {
        setStatusMessage(i18nt('invalid_email', 'Invalid Email address'));
      } else {
        // Call the Auth Signin API. TODO: Figure out if we want passwords
        setState(LoadingStateType.Loading);

        retryPromiseWithDelay({
          promiseFn: () =>
            auth.signin(newEmail, {
              callbackPathname: lState?.from?.pathname || '/'
            }),
          errorPredicate: shouldRetryQueryOnCommonErrors,
          retries: 8,
          delayTime: delayExponentialBackoff
        })
          .then(() => {
            setState(LoadingStateType.Verify);
          })
          .catch((err) => {
            console.log('signup Error', JSON.stringify(err));
            setState('');
            setStatusMessage(
              err.message || i18nt('error_logging_in', 'Error logging in')
            );
          });
      }
    } else {
      setStatusMessage(i18nt('email_required', 'Please enter your email'));
    }
  };

  // Key handler - checks for enter key
  const onKeyUp: KeyboardEventHandler = (event) => {
    const code = event.keyCode ? event.keyCode : event.which;
    if (code === 13) {
      onLogin();
    } else {
      setStatusMessage('');
    }
    return false;
  };

  // Render the UI

  return (
    <UnauthorizedPageLayout>
      <div className={styles.formContainer}>
        {(state === '' || state === LoadingStateType.Loading) && (
          <>
            <Typography.Title level={3} className={styles.welcomeText}>
              Welcome to Kognitos
            </Typography.Title>
            <Typography.Text className={styles.welcomeSub}>
              Log In to get started
            </Typography.Text>
            <div className={styles.form}>
              <Form.Item>
                <Input
                  ref={inputRef}
                  // className="email-input"
                  size="large"
                  placeholder={i18nt('enter_email', 'Email address')}
                  prefix={
                    <div className={styles.prefixIcon}>
                      {ComponentFactory.getIcon(AppConstants.ICONS.EMAIL)}
                    </div>
                  }
                  type="text"
                  id="email"
                  autoComplete="off" // KOG-637
                  spellCheck={false}
                  value={email}
                  onKeyUp={onKeyUp}
                  onChange={(e) => setEmail(e.target.value)}
                />

                {statusMessage.length ? (
                  <Text type="danger" className="status-message">
                    {statusMessage}
                  </Text>
                ) : null}
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={styles.loginButton}
                  disabled={
                    email.length === 0 ||
                    state === 'loading' ||
                    AppUtil.validateEmail(email) === false
                  }
                  onClick={onLogin}
                  icon={
                    LoadingStateType.Loading === state
                      ? ComponentFactory.getIcon(AppConstants.ICONS.LOADING)
                      : ''
                  }
                >
                  {state === LoadingStateType.Loading
                    ? 'Logging in...'
                    : 'Log In'}
                </Button>
              </Form.Item>
            </div>

            <Typography.Text className={styles.text}>
              {`Don't have an account?`} <NavLink to="/signup">Sign Up</NavLink>
            </Typography.Text>
          </>
        )}

        {state === LoadingStateType.Verify && (
          <VerificationLink email={email} />
        )}
      </div>
    </UnauthorizedPageLayout>
  );
}

export default Login;
